import {
  Box, Flex, Heading, Text, Button, FormControl, FormLabel, Input,
  InputGroup, InputRightElement
} from "@chakra-ui/react";
import { Loader, Eye, EyeOff } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

const AuthPage = ({ setUser, setToken }) => {
  const bg = "gray.50";
  const cardBg = "white";

  const location = useLocation();
  const navigate = useNavigate();

  const initialMode =
    new URLSearchParams(location.search).get("mode") === "login";
  const [isLogin, setIsLogin] = useState(initialMode);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // 🔑 toggle
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
  }, [isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Errore di autenticazione");
      }

      // Attendi token e profilo utente dal backend
      const receivedToken = data.token;
      const userData = data.user;

      // Salva in localStorage
      localStorage.setItem("token", receivedToken);
      localStorage.setItem("user", JSON.stringify(userData));

      // Aggiorna stato app
      setToken(receivedToken);
      setUser(userData);

      // Redirect alla dashboard
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex align="center" justify="center" minH="100vh" bg={bg} p={4}>
      <Box
        w="full"
        maxW="md"
        bg={cardBg}
        p={8}
        borderRadius="xl"
        shadow="2xl"
        borderTop="4px"
        borderColor="indigo.600"
      >
        <Heading
          as="h2"
          size="xl"
          fontWeight="bold"
          textAlign="center"
          color="indigo.700"
          mb={6}
        >
          {isLogin ? "Accedi al Tuo Account" : "Registrati Ora"}
        </Heading>

        {error && (
          <Box bg="red.100" color="red.700" p={3} borderRadius="lg" mb={4}>
            {error}
          </Box>
        )}

        <Box as="form" onSubmit={handleSubmit}>
          {/* Campo Email */}
          <FormControl id="email" mb={4}>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FormControl>

          {/* Campo Password con toggle */}
          <FormControl id="password" mb={2}>
            <FormLabel>Password</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <InputRightElement>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                </Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>

          {/* Link "Password dimenticata?" */}
          {isLogin && (
            <Text textAlign="right" fontSize="sm" color="indigo.600" mb={4}>
              <Button
                variant="link"
                size="sm"
                color="indigo.600"
                _hover={{ textDecoration: "underline" }}
                onClick={() => navigate("/reset-password")}
              >
                Password dimenticata?
              </Button>
            </Text>
          )}

          {/* Bottone Submit */}
          <Button type="submit" w="full" colorScheme="indigo" isLoading={isLoading}>
            {isLogin ? "Login" : "Crea Account"}
          </Button>
        </Box>

        {/* Switch login/register */}
        <Text mt={6} textAlign="center" fontSize="sm" color="gray.600">
          {isLogin ? "Non hai un account?" : "Hai già un account?"}{" "}
          <Button
            variant="link"
            size="sm"
            onClick={() => setIsLogin(!isLogin)}
            color="indigo.600"
            _hover={{ textDecoration: "underline" }}
          >
            {isLogin ? "Registrati" : "Accedi"}
          </Button>
        </Text>
      </Box>
    </Flex>
  );
};

export default AuthPage;
